import type { ValidationStageKey } from '@ispot/shared'
import type { ValidationAnalysisResponse } from '@/lib/api/client'

type ApiStageScore = ValidationAnalysisResponse['stageScores'][number]

interface ValidationStageRowProps {
  index: number
  stageKey: ValidationStageKey
  name: string
  question: string
  stageScore?: ApiStageScore
  totalSignalsFromCatalog: number
  isSelected: boolean
  onClick: () => void
}

function scoreToneClass(score: number): string {
  if (score >= 70) return 'text-success-fg'
  if (score >= 40) return 'text-warning-fg'
  return 'text-danger-fg'
}

export function ValidationStageRow({
  index,
  name,
  question,
  stageScore,
  totalSignalsFromCatalog,
  isSelected,
  onClick,
}: ValidationStageRowProps) {
  const score = stageScore?.confidenceScore ?? 0
  const signals = stageScore?.signals ?? []
  const totalSignals = signals.length || totalSignalsFromCatalog
  const validatedCount = signals.filter((s) => s.isValidated).length

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left grid items-center cursor-pointer transition-colors duration-[var(--dur-normal)] ${
        isSelected ? 'border-b-fg' : 'border-b-border'
      }`}
      style={{
        gridTemplateColumns: '24px 1fr 64px 120px',
        gap: 18,
        padding: '13px 0',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
      }}
    >
      <span
        className={`font-mono ${isSelected ? 'text-fg font-semibold' : 'text-fg-muted'}`}
        style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
      >
        {String(index).padStart(2, '0')}
      </span>

      <div className="flex items-baseline gap-3 min-w-0">
        <h3
          className="m-0 text-fg"
          style={{
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: '-0.005em',
          }}
        >
          {name}
        </h3>
        <p
          className="m-0 text-fg-muted truncate"
          style={{ fontSize: 12.5 }}
        >
          {question}
        </p>
      </div>

      <div
        className={`text-right ${scoreToneClass(score)}`}
        style={{
          fontSize: 17,
          fontWeight: 600,
          letterSpacing: '-0.015em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {score}
        <sup
          className="text-fg-muted"
          style={{ fontSize: 11, fontWeight: 500, marginLeft: 1 }}
        >
          %
        </sup>
      </div>

      <div className="flex items-center gap-2 justify-end">
        <span
          className="text-fg-muted font-mono"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.05em',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {validatedCount}/{totalSignals}
        </span>
        <div className="flex gap-0.5">
          {Array.from({ length: totalSignals }).map((_, i) => {
            const on = i < validatedCount
            return (
              <span
                key={i}
                className="rounded-full"
                style={{
                  width: 6,
                  height: 6,
                  background: on ? 'var(--color-fg)' : 'var(--color-bg-card)',
                  border: on
                    ? '1.25px solid var(--color-fg)'
                    : '1.25px solid var(--color-border-strong)',
                }}
              />
            )
          })}
        </div>
      </div>
    </button>
  )
}
